import React, { memo, useCallback, useState } from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Typography,
  Box,
  IconButton,
  Theme,
  useMediaQuery,
  Drawer,
  SvgIconTypeMap,
} from '@mui/material';
import { getCurrentRoute } from '../../services/mobileNavbarService';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SidebarItems } from './sidebarItems';

const style = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    pl: 2,
    pr: 2,
    width: '100vw',
    backgroundImage: `linear-gradient(to bottom right,${ColorPalette.TIFFANY_BLUE}, ${ColorPalette.CHINESE_BLACK})`,
    height: 50,
  },

  menuAndPageTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    color: ColorPalette.WHITE,
    cursor: 'pointer',
    ml: 0,
  },
  pageTitle: {
    color: ColorPalette.WHITE,
    ml: 2.5,
    fontWeight: '700',
    fontFamily: TypographyFamily.NEXA,
    pt: 0.5,
  },
  additionalButtons: {
    display: 'flex',
    alignItems: 'center',
  },
  drawer: {
    backgroundImage: `linear-gradient(${ColorPalette.TIFFANY_BLUE}, ${ColorPalette.CHINESE_BLACK})`,
    borderTopRightRadius: 10,
    borderBottomRightRadius: 10,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    overflow: 'hidden',
  },
  navbar: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    overflow: 'auto',

    pt: 3,
    ml: 1.5,
    mr: 1.5,
  },
  sidebar: {
    display: 'flex',
    height: '100vh',
    position: 'sticky',
    top: 0,
    backgroundImage: `linear-gradient(#02ada5, #1b182e)`,
  },

  roundCornersBox: {
    height: '100vh',
    width: 20,
    backgroundColor: '#fbfbfb',
    borderTopLeftRadius: 20,
    borderBottomLeftRadius: 20,
  },

  listNavigation: {
    p: 0,
    color: ColorPalette.WHITE,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
} as const;

interface Props {
  buttons?: HeaderButtons[];
}

export interface HeaderButtons {
  onClick(): void;
  icon: OverridableComponent<SvgIconTypeMap<unknown, 'svg'>>;
}

function HeaderBase({ buttons = [] }: Props): JSX.Element {
  const isMobile = useMediaQuery((themeMedia: Theme) =>
    themeMedia.breakpoints.down('sm')
  );

  const currentRoute = getCurrentRoute(location.pathname);
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);

  const onOpenDrawer = useCallback(() => {
    setIsOpenDrawer(true);
  }, []);

  const onCloseDrawer = useCallback(() => {
    setIsOpenDrawer(false);
  }, []);

  return (
    <>
      {isMobile && (
        <>
          <Box sx={style.header}>
            <Box sx={style.menuAndPageTitle}>
              <MenuIcon sx={style.icon} onClick={onOpenDrawer} />
              <Typography sx={style.pageTitle}>
                {currentRoute?.label}
              </Typography>
            </Box>

            <Box sx={style.additionalButtons}>
              {buttons.map((button, index) => {
                const key = String(index);

                return (
                  <IconButton onClick={button.onClick} key={key} sx={{ ml: 1 }}>
                    <button.icon
                      sx={{
                        ...style.icon,
                      }}
                      fontSize="small"
                    />
                  </IconButton>
                );
              })}
            </Box>
          </Box>

          <Drawer
            anchor={'left'}
            open={isOpenDrawer}
            onClose={onCloseDrawer}
            PaperProps={{
              sx: style.drawer,
            }}
          >
            <Box
              role="presentation"
              onClick={onCloseDrawer}
              sx={{ overflow: 'auto' }}
            >
              <SidebarItems onClose={onCloseDrawer} />
            </Box>
          </Drawer>
        </>
      )}

      {!isMobile && (
        <Box sx={style.sidebar}>
          <SidebarItems />
          <Box sx={style.roundCornersBox} />
        </Box>
      )}
    </>
  );
}

export const Header = memo(HeaderBase);
